import { useCallback, useEffect, useRef, useState } from 'react';
import type { Deck, DeckCard, SwipeDirection } from '@sfc/sdk';
import { platform } from '../api/client';

export type DeckState =
  | { status: 'loading' }
  | { status: 'unreachable' }
  | {
      status: 'ready';
      cards: DeckCard[];
      remaining: number;
      rankedByYourSkills: boolean;
      /** Set when a decision could not be recorded, cleared by the next one. */
      problem?: string;
    };

/**
 * Holds the deck, and decides about one card at a time.
 *
 * A decision is applied here before the server has answered, because a swipe
 * that waits for a round trip feels broken on a phone. The server's answer
 * then replaces the whole deck, which is also how a card posted a second ago
 * arrives without a reload.
 *
 * Every decision this browser has made is remembered for the life of the
 * screen and filtered out of whatever comes back. Two quick swipes overlap:
 * the first reply was computed before the second was recorded, so without that
 * filter the card somebody has just dismissed slides straight back in.
 */
export function useDeck(): {
  state: DeckState;
  decide: (ideaId: string, direction: SwipeDirection) => void;
  refresh: () => void;
} {
  const [state, setState] = useState<DeckState>({ status: 'loading' });
  const decided = useRef(new Set<string>());
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const apply = useCallback((deck: Deck, problem?: string) => {
    if (!live.current) return;
    const cards = deck.cards.filter((card) => !decided.current.has(card.ideaId));
    setState({
      status: 'ready',
      cards,
      // The server counted decisions it had recorded by then. Ones it had not
      // are still ours to subtract, or the count climbs back as you swipe.
      remaining: Math.max(deck.remaining - (deck.cards.length - cards.length), cards.length),
      rankedByYourSkills: deck.rankedByYourSkills,
      ...(problem ? { problem } : {}),
    });
  }, []);

  const load = useCallback(async () => {
    try {
      apply(await platform.match.deck());
    } catch {
      if (live.current) setState({ status: 'unreachable' });
    }
  }, [apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = useCallback(
    (ideaId: string, direction: SwipeDirection) => {
      if (decided.current.has(ideaId)) return;
      decided.current.add(ideaId);

      setState((current) => {
        if (current.status !== 'ready') return current;
        const cards = current.cards.filter((card) => card.ideaId !== ideaId);
        const { problem: _dropped, ...rest } = current;
        return { ...rest, cards, remaining: Math.max(current.remaining - 1, cards.length) };
      });

      void (async () => {
        try {
          apply(await platform.match.swipe(ideaId, direction));
        } catch {
          // The decision is gone from the screen but may not have been
          // recorded. Ask the server what it actually holds rather than
          // guessing, and keep the card out of the way while we find out.
          try {
            apply(await platform.match.deck(), 'That one may not have saved. The deck is up to date.');
          } catch {
            if (live.current) setState({ status: 'unreachable' });
          }
        }
      })();
    },
    [apply],
  );

  const refresh = useCallback(() => {
    setState({ status: 'loading' });
    void load();
  }, [load]);

  return { state, decide, refresh };
}
